import React, { useState, useEffect } from "react";
import CountryFlag from "../../common/CountryFlag";

export const PhoneInput = ({
  name = "phoneNumber",
  placeholder = "Phone Number",
  value = "",
  onChange,
  onBlur,
  disabled = false,
  label,
  isInvalid = false,
  invalidMessage,
  height = "50px",
  width = "100%",
  fontSize = "14px",
  countryCode = "JP",
  dialCode = "+81",
  onCountryChange,
  className = "",
  countriesJsonPath = "/json/countries.json",
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [countriesData, setCountriesData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({
    code: countryCode,
    dialCode: dialCode,
  });

  const heightValue = typeof height === 'number' ? `${height}px` : height;
  const widthValue = typeof width === 'number' ? `${width}px` : width;
  const fontSizeValue = typeof fontSize === 'number' ? `${fontSize}px` : fontSize;

  useEffect(() => {
    loadCountriesData();
  }, []);

  useEffect(() => {
    setSelectedCountry({
      code: countryCode,
      dialCode: dialCode,
    });
  }, [countryCode, dialCode]);

  const loadCountriesData = async () => {
    try {
      const response = await fetch(countriesJsonPath);
      const data = await response.json();
      setCountriesData(data);
    } catch (error) {
      console.error("Error loading countries data:", error);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry({
      code: country.code,
      dialCode: country.dial_code,
    });
    setIsDropdownOpen(false);
    setSearchQuery("");

    if (onCountryChange) {
      onCountryChange(country.code, country.dial_code, country.name);
    }
  };

  const filteredCountries = searchQuery
    ? countriesData.filter((country) => {
      const search = searchQuery.toLowerCase();
      return (
        country.name.toLowerCase().includes(search) ||
        country.dial_code.toLowerCase().includes(search)
      );
    })
    : countriesData;

  const labelStyle = {
    fontSize: '14px',
    color: '#282828',
    fontFamily: "'Hanken Grotesk', sans-serif",
    fontWeight: '400',
    lineHeight: '20px',
    marginBottom: '10px',
    display: 'block',
  };

  const containerStyle = {
    width: widthValue,
    marginBottom: '16px',
  };

  const inputContainerStyle = {
    display: 'flex',
    // gap: '10px',
    // marginLeft: '-7px',
    width: widthValue,
    height: heightValue,
    position: 'relative',
  };

  const countryButtonStyle = {
    backgroundColor: '#f9edd3',
    border: '1px solid #dbbe78',
    borderRadius: '5px',
    padding: '17px 11px',
    width: '100px',
    height: heightValue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: '12px',
    color: '#282828',
    outline: 'none',
    position: 'relative',
  };

  const phoneInputStyle = {
    flex: 1,
    height: heightValue,
    fontSize: fontSizeValue,
    backgroundColor: '#ffffff',
    border: '1px solid #e8e8e8',
    borderRadius: '5px',
    padding: '15px',
    color: '#282828',
    fontFamily: "'Hanken Grotesk', sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '300px',
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    border: '1px solid #e8e8e8',
    borderRadius: '5px',
    marginTop: '5px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
  };

  const searchInputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #e8e8e8',
    borderRadius: '5px',
    fontSize: '14px',
    fontFamily: "'Hanken Grotesk', sans-serif",
    outline: 'none',
    margin: '8px 0',
  };

  const countryItemStyle = {
    padding: '10px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    fontFamily: "'Hanken Grotesk', sans-serif",
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s',
  };

  // Down arrow SVG icon
  const DownArrowIcon = () => (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
      <path d="M1 1L5 5L9 1" stroke="#282828" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );

  return (
    <div style={containerStyle} className={className}>
      {label && (
        <label style={labelStyle} htmlFor={name}>
          {label}
        </label>
      )}
      <div style={inputContainerStyle}>
        {/* Country Code Button */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            style={countryButtonStyle}
          >
            <CountryFlag
              code={selectedCountry.code}
              emoji={countriesData?.find((c) => c.code === selectedCountry.code)?.flag}
              alt={selectedCountry.code}
              style={{
                width: '24px',
                height: '24px',
                border: '0.5px solid #f9edd3',
                borderRadius: '1px',
              }}
            />
            <span style={{ fontSize: '12px' }}>
              ({selectedCountry.dialCode})
            </span>
            <div style={{
              transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}>
              <DownArrowIcon />
            </div>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                onClick={() => setIsDropdownOpen(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
              />
              <div style={dropdownStyle}>
                <div style={{ padding: '8px' }}>
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={searchInputStyle}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div>
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <div
                        key={country.code}
                        onClick={() => handleCountrySelect(country)}
                        style={countryItemStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f6f6f6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                      >
                        <CountryFlag
                          code={country.code}
                          emoji={country.flag}
                          alt={country.name}
                          style={{
                            width: '24px',
                            height: '24px',
                          }}
                        />
                        <span>
                          ({country.dial_code}) {country.name}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#a7a7a7' }}>
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          id={name}
          type="tel"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          style={{
            ...phoneInputStyle,
            borderColor: isInvalid ? '#dc3545' : '#e8e8e8',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#dbbe78';
          }}
          onBlurCapture={(e) => {
            if (!isInvalid) {
              e.target.style.borderColor = '#e8e8e8';
            }
          }}
        />
      </div>
      {isInvalid && invalidMessage && (
        <p
          style={{
            color: '#dc3545',
            fontSize: '12px',
            marginTop: '5px',
            marginBottom: '0',
          }}
        >
          {invalidMessage}
        </p>
      )}
    </div>
  );
};
